import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CookieBanner.css';

const COOKIE_NAME = 'runner_cookie_consent';
const COOKIE_VALUE = 'acknowledged';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

type SupportedLang = 'ko' | 'en';

function resolveLang(raw: string | undefined): SupportedLang {
  return raw === 'en' ? 'en' : 'ko';
}

function hasConsent(): boolean {
  if (typeof document === 'undefined') return true;
  return document.cookie.split(';').some((part) => part.trim() === `${COOKIE_NAME}=${COOKIE_VALUE}`);
}

export function CookieBanner() {
  const { t } = useTranslation('common');
  const { lang: langParam } = useParams<{ lang?: string }>();
  const lang = resolveLang(langParam);
  const [visible, setVisible] = useState(() => !hasConsent());

  const accept = useCallback(() => {
    document.cookie = `${COOKIE_NAME}=${COOKIE_VALUE}; max-age=${ONE_YEAR_SECONDS}; path=/; SameSite=Lax`;
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="region" aria-label="Cookie notice">
      <p className="cookie-banner-message">{t('cookie.message')}</p>
      <div className="cookie-banner-actions">
        <Link to={`/${lang}/privacy`} className="cookie-banner-link">
          {t('cookie.details')}
        </Link>
        <button type="button" className="cookie-banner-accept" onClick={accept}>
          {t('cookie.accept')}
        </button>
      </div>
    </div>
  );
}
